import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Logo, cn } from "@nexirt/ui";
import {
  Command as CmdIcon,
  Github,
  ArrowRight,
  ChevronDown,
  Search as SearchIcon,
} from "lucide-react";
import { business } from "../content/business";
import { BusinessHours } from "./BusinessHours";
import { getMe } from "../lib/auth";
import UserAvatarMenu from "./UserAvatarMenu";
import FooterNewsletter from "./FooterNewsletter";
import StatusPill from "./StatusPill";
import MegaMenu from "./MegaMenu";
import CommandPalette from "./CommandPalette";
import MobileDrawer from "./MobileDrawer";

interface NavMe {
  label: string;
}

/**
 * Dispatch the same Cmd-K keydown event that GlobalPalette listens
 * for, so any button can trigger the palette without coupling to its
 * internal API. Used by the footer ⌘K button (rev 21 Loop B5).
 */
function openCommandPalette() {
  window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
}

// Lazy-load ChatWidget so the initial Layout bundle isn't bloated by
// the chat surface (most visitors don't open it). Suspense fallback
// renders nothing — the floating button mounts on first frame after
// the chunk arrives.
const ChatWidget = lazy(() => import("./ChatWidget"));

// Rev 25 Phase 2 Loop C — desktop nav per Navigation.html artboard:
// Work · Services (mega-menu trigger in Loop D) · Process · Pricing · Journal (hidden).
// `to: null` entries don't render — Journal stays in this array so future
// content-on-/journal just flips the to-value back to "/journal".
const NAV: { to: string | null; label: string; badge?: "new" }[] = [
  { to: "/work", label: "Work" },
  { to: "/services", label: "Services" },
  { to: "/process", label: "Process" },
  { to: "/pricing", label: "Pricing" },
  // TODO(journal): re-enable Journal link when /journal content exists.
  { to: null, label: "Journal", badge: "new" },
];

export default function Layout() {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [me, setMe] = useState<NavMe | null>(null);
  const servicesRef = useRef<HTMLSpanElement | null>(null);
  const megaGraceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => setMegaOpen(false), [pathname]);

  const openMega = () => {
    if (megaGraceTimer.current) clearTimeout(megaGraceTimer.current);
    setMegaOpen(true);
  };
  const closeMegaWithGrace = () => {
    if (megaGraceTimer.current) clearTimeout(megaGraceTimer.current);
    megaGraceTimer.current = setTimeout(() => setMegaOpen(false), 200);
  };

  // Rev 21 Loop B4 / rev 23 Loop A0-auth-nav — Cognito session
  // resolves the authed branch (UserAvatarMenu). Anonymous gets a
  // Sign-in CTA on the right that visually balances the N NEXIRT
  // logo on the left. Rev 25.3 retired the top-level PORTAL pill;
  // /portal is reachable via the avatar dropdown.
  useEffect(() => {
    let cancelled = false;
    getMe().then((resp) => {
      if (cancelled) return;
      if (resp) {
        setAuthed(true);
        const u = resp.user as { signInDetails?: { loginId?: string }; username?: string };
        setMe({ label: u.signInDetails?.loginId ?? u.username ?? "Account" });
      } else {
        setAuthed(false);
        setMe(null);
      }
    });
    return () => { cancelled = true; };
  }, [pathname]);

  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>
      <header
        data-site-header
        className="sticky top-0 z-[var(--nx-z-nav)] border-b border-nx-line bg-nx-bg/55 backdrop-blur-md supports-[backdrop-filter]:bg-nx-bg/40"
      >
        <div className="nx-container flex h-16 items-center gap-4">
          <Link to="/" aria-label="NEXIRT home" className="shrink-0">
            <Logo variant="horizontal" size="md" />
          </Link>

          {/* Desktop nav links — md+ only. Rev 25 Phase 2 Loop C + D
              (Services is the MegaMenu trigger on hover/focus). */}
          <nav
            className="hidden items-center gap-1 md:flex"
            aria-label="Primary"
            data-nav-primary
          >
            {NAV.map((n) => {
              if (n.to === null) return null;
              const isServices = n.label === "Services";
              if (isServices) {
                return (
                  <span
                    key="services"
                    ref={servicesRef}
                    className="relative"
                    onMouseEnter={openMega}
                    onMouseLeave={closeMegaWithGrace}
                    onFocus={openMega}
                  >
                    <NavLink
                      to={n.to}
                      role="button"
                      aria-haspopup="menu"
                      aria-expanded={megaOpen}
                      onClick={(e) => {
                        // Allow click to also open/close mega menu on
                        // touch devices that don't fire mouseenter.
                        if (!megaOpen) {
                          e.preventDefault();
                          openMega();
                        }
                      }}
                      className={({ isActive }) =>
                        `inline-flex items-center gap-1.5 rounded-nx2 px-3 py-1.5 text-[13.5px] transition-colors ${
                          isActive || megaOpen
                            ? "bg-white/[0.04] text-nx-text"
                            : "text-nx-text-dim hover:bg-white/[0.03] hover:text-nx-text"
                        }`
                      }
                    >
                      {n.label}
                      <ChevronDown
                        className={cn(
                          "h-3 w-3 transition-transform duration-200",
                          megaOpen ? "rotate-180" : "",
                        )}
                        strokeWidth={2}
                        aria-hidden
                      />
                    </NavLink>
                    <MegaMenu
                      open={megaOpen}
                      onClose={() => setMegaOpen(false)}
                      triggerRef={servicesRef}
                    />
                  </span>
                );
              }
              return (
                <NavLink
                  key={n.label}
                  to={n.to}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 rounded-nx2 px-3 py-1.5 text-[13.5px] transition-colors ${
                      isActive
                        ? "bg-white/[0.04] text-nx-text"
                        : "text-nx-text-dim hover:bg-white/[0.03] hover:text-nx-text"
                    }`
                  }
                >
                  {n.label}
                  {n.badge === "new" ? (
                    <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-nx-accent-2 bg-nx-accent-2-soft border border-nx-accent-2/30 px-1.5 py-0.5 rounded-[4px]">
                      new
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex-1" />

          {/* ⌘K mock search input — clickable surface that will trigger
              CommandPalette in Loop E. Currently a no-op for tablet+. */}
          <button
            type="button"
            data-nav-cmdk-trigger
            onClick={openCommandPalette}
            aria-label="Open command palette"
            className="hidden h-9 min-w-[200px] items-center gap-2.5 rounded-nx2 border border-nx-line bg-nx-surface/70 px-3 text-[12.5px] text-nx-text-dim transition-colors hover:border-nx-line-strong md:inline-flex lg:min-w-[280px]"
          >
            <SearchIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
            <span className="flex-1 text-left">
              <span className="hidden lg:inline">Ask the assistant…</span>
              <span className="lg:hidden">Ask…</span>
            </span>
            <span className="inline-flex items-center gap-0.5 rounded-[4px] border border-nx-line-strong bg-nx-surface px-1.5 py-0.5 font-mono text-[10.5px] text-nx-text-faint">
              <CmdIcon className="h-2.5 w-2.5" strokeWidth={1.8} />
              <span>K</span>
            </span>
          </button>

          {/* StatusPill — premium-scarcity signal. Compact at md, full at lg+. */}
          <span className="hidden md:inline-flex lg:hidden">
            <StatusPill variant="compact" />
          </span>
          <span className="hidden lg:inline-flex">
            <StatusPill />
          </span>

          {/* Auth surface — anonymous Sign-in OR Portal+Avatar; carries
              from rev 23 Loop A0-auth-nav. Hidden on mobile (MobileDrawer
              from Loop F will surface these). */}
          {authed && me ? (
            // Rev 25.3 — PORTAL pill (data-nav-portal) removed; not in
            // docs/NEXIRT_DESIGN/Navigation.html. UserAvatarMenu's
            // dropdown still has a "Portal" item so authed desktop users
            // reach /portal via the avatar, not a redundant top-level pill.
            <div className="hidden md:block">
              <UserAvatarMenu me={me} />
            </div>
          ) : (
            <Link
              to="/portal/login"
              className="hidden items-center whitespace-nowrap rounded-nx2 border border-nx-line-strong bg-transparent px-3 py-1.5 text-[13px] text-nx-text transition-colors hover:bg-nx-surface-2 md:inline-flex"
              data-nav-signin
            >
              Sign in
            </Link>
          )}

          {/* Start a project — primary CTA. Visible on all viewports;
              compact label on mobile per Navigation.html artboard. */}
          <Link
            to="/contact"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-nx2 bg-nx-accent px-3 py-1.5 text-[13px] font-medium text-white shadow-[var(--nx-shadow-glow)] transition-[transform,filter] duration-150 ease-nx hover:-translate-y-[1px] hover:brightness-110 md:px-3.5"
            data-nav-start
          >
            <span className="hidden md:inline">Start a project</span>
            <span className="md:hidden">Start</span>
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
          </Link>

          {/* Mobile burger — drawer redesigned in Loop F. */}
          <button
            className="rounded-nx2 border border-nx-line-strong p-2 md:hidden"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden>
              <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </header>

      {/* Rev 25 Phase 2 Loop F — replaces the rev-21 inline mobile
          drop-down menu. Right-slide drawer with search + nav rows +
          account section + StatusPill+Start CTA footer. */}
      <MobileDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        authed={authed}
        me={me}
        onSignOut={async () => {
          const { signOut } = await import("../lib/auth");
          await signOut();
          window.location.assign("/");
        }}
        onOpenChat={() => {
          window.dispatchEvent(
            new CustomEvent("nexirt:copilot:open", { detail: {} }),
          );
        }}
      />

      <main id="main">
        {/* Non-home marketing routes are lazy chunks (App.tsx). The
            fallback reserves viewport height so the footer doesn't jump
            up for the few ms a route chunk takes to arrive. */}
        <Suspense fallback={<div className="min-h-[100dvh]" aria-busy="true" />}>
          <Outlet />
        </Suspense>
      </main>

      <SiteFooter />

      {/* Rev 25 Phase 2 Loop E — CommandPalette replaces the rev-15
          GlobalPalette. Same ⌘K global keybinding; the existing footer
          ⌘K trigger from rev 21 Loop B5 still works (it dispatches a
          synthetic Cmd-K KeyboardEvent that CommandPalette's global
          listener handles). */}
      <CommandPalette authed={authed} />

      {/* Custom AI chatbot — rev 18 Loop B. Lazy-loaded; floating
          launcher mounts on first frame after the chunk arrives. */}
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
    </>
  );
}

/**
 * Marketing-site footer — Rev 25 Phase 2 Loop G rebuild per Home.html
 * footer markup. Four columns at desktop, 2x2 at tablet, single column
 * at mobile.
 *
 * Brand · Studio · Engage · Elsewhere. Bottom bar carries build metadata,
 * copyright, ⌘K trigger (rev-21 redundant entry kept), PoweredBy (tenant-
 * conditional).
 */
function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-nx-line/60 bg-nx-surface/40" data-site-footer>
      <div className="nx-container grid grid-cols-1 gap-10 py-14 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
        {/* Brand */}
        <div className="max-w-[300px]">
          <Logo variant="horizontal" size="md" />
          <p className="mt-4 text-sm leading-relaxed text-nx-text-dim">
            Custom software studio. Founder Newton Cazzaro · 20+ years
            AWS-native · senior engineering bench.
          </p>
          <div className="mt-5">
            <StatusPill />
          </div>
          <div className="mt-5 flex items-center gap-2.5" data-footer-social>
            <SocialIconButton
              href="https://www.linkedin.com/company/nexirt"
              label="NEXIRT on LinkedIn"
              brand="linkedin"
            >
              <LinkedInGlyph />
            </SocialIconButton>
            <SocialIconButton
              href="https://www.instagram.com/nexirt_com/"
              label="NEXIRT on Instagram"
              brand="instagram"
            >
              <InstagramGlyph />
            </SocialIconButton>
            <SocialIconButton
              href="https://github.com/NewtonCazzaro"
              label="Newton Cazzaro on GitHub"
              brand="github"
            >
              <Github className="h-[18px] w-[18px]" strokeWidth={1.7} />
            </SocialIconButton>
          </div>
          <FooterNewsletter />
        </div>

        {/* Studio */}
        <FooterCol
          title="Studio"
          links={[
            { to: "/work", label: "Work" },
            { to: "/services", label: "Services" },
            { to: "/labs", label: "Labs" },
            { to: "/process", label: "Process" },
            // TODO(journal): re-enable when /journal content exists.
            { to: "/about", label: "About" },
          ]}
        />

        {/* Engage */}
        <FooterCol
          title="Engage"
          links={[
            { to: "/pricing", label: "Pricing" },
            { to: "/pricing#care", label: "Care plans" },
            { to: "/contact", label: "Contact" },
            { to: "/status", label: "Status" },
          ]}
        />

        {/* Elsewhere */}
        <FooterCol
          title="Elsewhere"
          links={[
            { to: "https://github.com/NewtonCazzaro", label: "GitHub", external: true },
            { to: "https://www.linkedin.com/in/newton-cazzaro", label: "LinkedIn", external: true },
            { to: "https://read.cv/newton", label: "Read.cv", external: true },
            // TODO(rss): re-enable RSS when /journal ships + /journal/feed.xml exists.
          ]}
        />
      </div>

      {/* Bottom bar — build metadata + copyright + ⌘K (rev-21 redundant
          entry) + PoweredBy (tenant-conditional). Address line carried
          forward in compact form for SEO + business contact. */}
      <div className="border-t border-nx-line/60">
        <div className="nx-container flex flex-col items-start justify-between gap-3 py-5 font-mono text-[10.5px] uppercase tracking-[0.14em] text-nx-text-faint md:flex-row md:items-center">
          <address className="not-italic">
            <span className="text-nx-text-dim">{business.name}</span>
            <span className="mx-2 text-nx-line-strong">·</span>
            <span>{business.address.city}, {business.address.region}</span>
            <span className="mx-2 text-nx-line-strong">·</span>
            <a href={`mailto:${business.contact.email}`} className="hover:text-nx-accent">
              {business.contact.email}
            </a>
            <span className="mx-2 hidden text-nx-line-strong md:inline">·</span>
            <BusinessHours compact className="hidden text-[10.5px] md:inline" />
          </address>
          <div className="flex flex-wrap items-center gap-3">
            <span data-footer-build>v25 · phase-2 · us-east-1</span>
            <span className="text-nx-line-strong">·</span>
            <span>© {new Date().getFullYear()} NEXIRT · all rights reserved</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={openCommandPalette}
              data-footer-cmdk
              aria-label="Open command palette"
              className="inline-flex items-center gap-1.5 rounded-nx2 border border-nx-line bg-nx-surface/60 px-2 py-1 text-nx-text-dim transition-colors hover:border-nx-line-strong hover:text-nx-text"
            >
              <CmdIcon className="h-3 w-3" />
              <span className="font-mono">K</span>
              <span className="hidden sm:inline">Search</span>
            </button>
            <PoweredByNexirt />
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * Branded footer social icon — Rev 27 Loop H.
 *
 * Default: dim icon, nx-line border, nx-surface bg.
 * Hover / focus-visible:
 *   - purple→teal gradient fill (matches --nx-gradient-head family)
 *   - icon flips to white
 *   - -1px lift + accent-glow drop shadow
 *   - diagonal white sheen sweeps left→right (cosmetic; motion-safe)
 *
 * Reduce-motion users get the gradient swap + color change but no
 * transform and no sheen sweep. Focus-visible mirrors hover for
 * keyboard a11y; ring uses nx-accent + nx-bg offset.
 *
 * Data attributes:
 *   - data-footer-social-link → kept for backwards compat with
 *     prior e2e selectors.
 *   - data-social={brand}     → lets analytics + Playwright target
 *     individual brands without coupling to icon DOM.
 */
function SocialIconButton({
  href,
  label,
  brand,
  children,
}: {
  href: string;
  label: string;
  brand: "linkedin" | "instagram" | "github";
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-footer-social-link
      data-social={brand}
      className={cn(
        "group relative inline-flex h-10 w-10 items-center justify-center overflow-hidden rounded-nx2",
        "border border-nx-line bg-nx-surface text-nx-text-dim",
        "transition-[color,border-color,transform,box-shadow] duration-[220ms] ease-nx",
        "hover:-translate-y-[1px] hover:text-white hover:border-transparent",
        "hover:shadow-[0_10px_28px_-6px_var(--nx-accent-glow)]",
        "focus-visible:-translate-y-[1px] focus-visible:text-white focus-visible:border-transparent",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nx-accent focus-visible:ring-offset-2 focus-visible:ring-offset-nx-bg",
        "motion-reduce:transform-none motion-reduce:transition-[color,border-color,box-shadow]",
      )}
    >
      {/* Gradient fill — appears on hover/focus. Sits behind the icon so
          currentColor (→ white on hover) renders on top. */}
      <span
        aria-hidden="true"
        className={cn(
          "absolute inset-0 opacity-0 transition-opacity duration-[220ms] ease-nx",
          "group-hover:opacity-100 group-focus-visible:opacity-100",
          "bg-[linear-gradient(135deg,var(--nx-accent)_0%,var(--nx-accent-2)_100%)]",
        )}
      />
      {/* Diagonal white sheen — sweeps L→R on hover. Pure cosmetic;
          hidden for reduce-motion users (gradient still swaps). */}
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-y-0 -left-full w-full",
          "bg-[linear-gradient(115deg,transparent_0%,rgba(255,255,255,0.18)_50%,transparent_100%)]",
          "transition-transform duration-[600ms] ease-nx",
          "group-hover:translate-x-[200%] group-focus-visible:translate-x-[200%]",
          "motion-reduce:hidden",
        )}
      />
      <span className="relative inline-flex">{children}</span>
    </a>
  );
}

/**
 * LinkedIn brand glyph — official "in" mark, 24×24 viewBox.
 * fill=currentColor so the icon picks up text color (dim → white on
 * hover) without per-state SVG swaps.
 */
function LinkedInGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className="h-[18px] w-[18px]"
    >
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.59 0 4.25 2.36 4.25 5.43v6.31zM5.34 7.43c-1.14 0-2.06-.93-2.06-2.06s.92-2.06 2.06-2.06 2.06.92 2.06 2.06-.92 2.06-2.06 2.06zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.78C.8 0 0 .78 0 1.74v20.51C0 23.21.8 24 1.78 24h20.44C23.2 24 24 23.21 24 22.25V1.74C24 .78 23.2 0 22.22 0z" />
    </svg>
  );
}

/**
 * Instagram brand glyph — rounded-square frame + lens + dot. Stroke-
 * based so the icon stays crisp at 18px and tints with currentColor.
 */
function InstagramGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-[18px] w-[18px]"
    >
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * "Powered by NEXIRT" — Phase 13 Loop 1. Always shown on nexirt.com
 * (the apex marketing site). On tenant subdomains, hidden only when
 * the tenant's active plan is Partner AND branding.hidePoweredBy is
 * true. Client-side gating is defense-in-depth only; the server-side
 * entitlements check in api-billing gates the branding config flag.
 */
function PoweredByNexirt() {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const isTenantSubdomain = hostname.endsWith(".app.nexirt.com");
  if (!isTenantSubdomain) return null;
  const tenant = typeof window !== "undefined" ? window.__nexirt_tenant : undefined;
  const branding = tenant?.branding as (Record<string, unknown> & { hidePoweredBy?: boolean }) | null | undefined;
  const hidden = branding?.hidePoweredBy === true; // server-enforced: only Partner tenants can set this
  if (hidden) return null;
  return (
    <a
      href="https://nexirt.com"
      className="text-xs text-nx-text-dim hover:text-nx-text"
      data-powered-by-nexirt
      rel="noopener"
    >
      Powered by <span className="font-semibold text-nx-text">NEXIRT</span>
    </a>
  );
}

function FooterCol({
  title,
  links,
}: {
  title: string;
  links: { to: string; label: string; external?: boolean }[];
}) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-nx-text-faint">
        {title}
      </div>
      <ul className="mt-3 space-y-2">
        {links.map((l) =>
          l.external ? (
            <li key={l.to}>
              <a
                href={l.to}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-nx-text hover:text-nx-accent"
              >
                {l.label}
              </a>
            </li>
          ) : (
            <li key={l.to}>
              <Link to={l.to} className="text-sm text-nx-text hover:text-nx-accent">
                {l.label}
              </Link>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}
